import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete, Edit, Search } from '@mui/icons-material';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { PaginationMeta, User } from '../../types';

type StatusMessage = { type: 'success' | 'error'; text: string };

const DEFAULT_PER_PAGE = 15;

const UserManagementPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canViewUsers = hasPermission('admin.users.view');
  const canUpdateUserStatus = hasPermission('admin.user_status.manage');

  const [filters, setFilters] = useState({
    search: '',
    isVerified: 'all',
    isActive: 'all',
    page: 1,
    perPage: DEFAULT_PER_PAGE,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [adminRoles, setAdminRoles] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: null as number | null,
    name: '',
    email: '',
    phone: '',
    password: '',
    roles: [] as string[],
    is_active: true,
    is_verified: true,
  });
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const stats = useMemo(
    () => ({
      total: pagination?.total ?? users.length,
      active: users.filter((user) => user.is_active).length,
      verified: users.filter((user) => user.is_verified).length,
    }),
    [pagination, users]
  );

  const buildParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page: filters.page,
      per_page: filters.perPage,
    };
    if (filters.search.trim()) {
      params.search = filters.search.trim();
    }
    if (filters.isActive !== 'all') {
      params.is_active = filters.isActive === 'true';
    }
    if (filters.isVerified !== 'all') {
      params.is_verified = filters.isVerified === 'true';
    }
    return params;
  }, [filters]);

  const fetchUsers = useCallback(async () => {
    if (!canViewUsers) {
      setUsers([]);
      setPagination(null);
      return;
    }
    setLoading(true);
    try {
      const response = await apiService.getAdminPortalUsers(buildParams);
      setUsers(response.data);
      setPagination(response.pagination);
    } catch (error: any) {
      console.error('Failed to load admin users:', error);
      setStatusMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Unable to load admin users.',
      });
    } finally {
      setLoading(false);
    }
  }, [buildParams, canViewUsers]);

  const loadRoles = useCallback(async () => {
    try {
      const data = await apiService.getAdminRoles();
      setAdminRoles(data);
    } catch (error) {
      console.error('Failed to load admin roles:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleFilterChange = (field: keyof typeof filters, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? Number(value) : 1,
    }));
  };

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    handleFilterChange('page', page);
  };

  const handlePerPageChange = (event: SelectChangeEvent<number>) => {
    handleFilterChange('perPage', Number(event.target.value));
  };

  const openForm = (mode: 'create' | 'edit', user?: User) => {
    setFormMode(mode);
    setDialogError(null);
    if (mode === 'create' || !user) {
      setFormData({
        id: null,
        name: '',
        email: '',
        phone: '',
        password: '',
        roles: [],
        is_active: true,
        is_verified: true,
      });
      setFormOpen(true);
      return;
    }

    setFormLoading(true);
    apiService
      .getAdminUser(user.id)
      .then((fresh) => {
        setFormData({
          id: fresh.id,
          name: fresh.name,
          email: fresh.email,
          phone: fresh.phone,
          password: '',
          roles: fresh.roles?.map((role) => role.name) ?? [],
          is_active: fresh.is_active,
          is_verified: fresh.is_verified,
        });
        setFormOpen(true);
      })
      .catch((error: any) => {
        console.error('Failed to load admin user:', error);
        setStatusMessage({
          type: 'error',
          text: error?.response?.data?.message || 'Unable to load admin user.',
        });
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const closeForm = () => {
    setFormOpen(false);
    setDialogError(null);
  };

  const handleFormSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setDialogError('Name, email, and phone are required.');
      return;
    }
    if (formMode === 'create' && !formData.password) {
      setDialogError('Password is required for new admin users.');
      return;
    }

    const payload: {
      name?: string;
      email?: string;
      phone?: string;
      password?: string;
      roles?: string[];
      is_active?: boolean;
      is_verified?: boolean;
    } = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      roles: formData.roles,
      is_active: formData.is_active,
      is_verified: formData.is_verified,
    };

    if (formMode === 'create' || formData.password) {
      payload.password = formData.password;
    }

    setFormLoading(true);
    try {
      if (formMode === 'create') {
        await apiService.createAdminUser(payload as any);
        setStatusMessage({ type: 'success', text: 'Admin user created successfully.' });
      } else if (formData.id) {
        await apiService.updateAdminUser(formData.id, payload);
        setStatusMessage({ type: 'success', text: 'Admin user updated successfully.' });
      }
      await fetchUsers();
      closeForm();
    } catch (error: any) {
      console.error('Failed to save admin user:', error);
      setDialogError(error?.response?.data?.message || 'Unable to save admin user.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteAdminUser(deleteTarget.id);
      setStatusMessage({ type: 'success', text: 'Admin user deleted.' });
      await fetchUsers();
      setDeleteTarget(null);
    } catch (error: any) {
      console.error('Failed to delete admin user:', error);
      setStatusMessage({ type: 'error', text: error?.response?.data?.message || 'Unable to delete admin user.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      isVerified: 'all',
      isActive: 'all',
      page: 1,
      perPage: DEFAULT_PER_PAGE,
    });
  };

  if (!canViewUsers) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom>
          Admin User Management
        </Typography>
        <Typography color="text.secondary">
          You do not have sufficient permissions to view this section.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Admin User Management</Typography>
        <Button variant="contained" onClick={() => openForm('create')}>
          Create Admin User
        </Button>
      </Stack>
      {statusMessage && (
        <Alert severity={statusMessage.type} onClose={() => setStatusMessage(null)} sx={{ mb: 2 }}>
          {statusMessage.text}
        </Alert>
      )}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search name, email, phone"
              value={filters.search}
              onChange={(event) => handleFilterChange('search', event.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Verified</InputLabel>
              <Select
                label="Verified"
                value={filters.isVerified}
                onChange={(event) => handleFilterChange('isVerified', event.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Active</InputLabel>
              <Select
                label="Active"
                value={filters.isActive}
                onChange={(event) => handleFilterChange('isActive', event.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Rows</InputLabel>
              <Select value={filters.perPage} onChange={handlePerPageChange} label="Rows">
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button variant="text" onClick={resetFilters}>
              Reset
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Chip label={`Total: ${stats.total}`} color="primary" />
        <Chip label={`Active: ${stats.active}`} color="success" />
        <Chip label={`Verified: ${stats.verified}`} color="info" />
      </Stack>
      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name & Contact</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography align="center" color="text.secondary">
                        No admin users match the filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2">{user.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.phone}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {user.roles?.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {user.roles.map((role) => (
                              <Chip key={role.id} label={role.name} size="small" />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No roles
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_verified ? 'Verified' : 'Unverified'}
                          color={user.is_verified ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Active' : 'Inactive'}
                          color={user.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" title="Edit user" onClick={() => openForm('edit', user)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" title="Delete user" onClick={() => setDeleteTarget(user)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              count={Math.max(pagination?.last_page ?? 1, 1)}
              page={filters.page}
              onChange={handlePaginationChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="sm">
        <DialogTitle>{formMode === 'create' ? 'Create Admin User' : 'Edit Admin User'}</DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Email"
              value={formData.email}
              onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))}
              fullWidth
            />
            <TextField
              label={formMode === 'create' ? 'Password' : 'New password (leave blank to keep existing)'}
              type="password"
              value={formData.password}
              onChange={(event) => setFormData((prev) => ({ ...prev, password: event.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="admin-roles-label">Roles</InputLabel>
              <Select
                labelId="admin-roles-label"
                multiple
                value={formData.roles}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    roles:
                      typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value,
                  }))
                }
                label="Roles"
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {adminRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    <Checkbox checked={formData.roles.includes(role)} />
                    <ListItemText primary={role} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(_, checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_verified}
                    onChange={(_, checked) => setFormData((prev) => ({ ...prev, is_verified: checked }))}
                  />
                }
                label="Verified"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Cancel</Button>
          <Button onClick={handleFormSubmit} disabled={formLoading}>
            {formMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Admin User</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
